import React, { createContext, useContext, useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import useCurrentUser from "../features/user-management/hooks/useCurrentUser";
import { useHasBusinessProfiles } from "../hooks/useHasBusinessProfiles";
import { UserGroup } from "../types/UserGroup";

type OnboardingGateContextType = {
  markOnboardingComplete: () => void;
};

const OnboardingGateContext = createContext<OnboardingGateContextType>({
  markOnboardingComplete: () => {},
});

export const useOnboardingGate = () => useContext(OnboardingGateContext);

const OnboardingGate: React.FC = () => {
  const { user, loading, idToken, userGroups } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [bypassGate, setBypassGate] = useState(false);

  // A "pure" Customer (not SuperAdmin/Internal). Admins may legitimately have
  // zero business profiles and must never be forced into onboarding.
  const isCustomerUser =
    Boolean(userGroups?.includes(UserGroup.CUSTOMER)) &&
    !userGroups?.includes(UserGroup.SUPERADMIN) &&
    !userGroups?.includes(UserGroup.INTERNAL);

  // A Customer with zero business profiles has nothing to do on the agent
  // board — the empty "Your agents" page is a dead end for a first-time user.
  const profilesEnabled = !loading && user !== null && isCustomerUser && !bypassGate;
  const { status: profileStatus, resumeBrandId } = useHasBusinessProfiles(idToken, profilesEnabled);

  const devForceOnboarding =
    process.env.NODE_ENV !== "production" &&
    new URLSearchParams(window.location.search).get("force_onboarding") === "true";

  const needsOnboarding =
    !bypassGate &&
    user !== null &&
    (user.profile_created === false ||
      profileStatus === "no-profiles" ||
      profileStatus === "needs-details" ||
      devForceOnboarding);

  const isAlreadyOnOnboarding = location.pathname === "/onboarding-agent";

  // While we're still resolving whether a Customer has any profiles, hold the
  // spinner instead of flashing the empty agent board.
  const stillDeciding =
    profilesEnabled &&
    user.profile_created !== false &&
    (profileStatus === "idle" || profileStatus === "checking");

  useEffect(() => {
    if (!loading && needsOnboarding && !isAlreadyOnOnboarding) {
      // A Brand left after step 1 reopens on step 2 for it (SGL-512).
      const resume = profileStatus === "needs-details" && resumeBrandId ? `?brand=${encodeURIComponent(resumeBrandId)}` : "";
      navigate(`/onboarding-agent${resume}`, { replace: true });
    }
  }, [loading, needsOnboarding, isAlreadyOnOnboarding, navigate, profileStatus, resumeBrandId]);

  // Show spinner while user loads, while a redirect is about to fire, or while
  // we're still deciding — all to prevent any flash of the empty agent board.
  if (
    loading ||
    (needsOnboarding && !isAlreadyOnOnboarding) ||
    (stillDeciding && !isAlreadyOnOnboarding)
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#eae6da] dark:bg-theme-background">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-[#2f4941] dark:border-brand-primary" />
      </div>
    );
  }

  return (
    <OnboardingGateContext.Provider
      value={{ markOnboardingComplete: () => setBypassGate(true) }}
    >
      <Outlet />
    </OnboardingGateContext.Provider>
  );
};

export default OnboardingGate;
